/**
 * O texto editado no documento da mesa e o markup mínimo que o servidor converte em texto em partes
 * (E202.F2, GUS-145, ADR-0145 §4). Espelha `apps/api/services/texto_editado.py`:
 *
 * - `**negrito**`;
 * - a linha que começa por `- ` é um item de lista (a continuação do item vem com dois espaços);
 * - a linha em branco separa parágrafos; a quebra simples é a quebra de linha no parágrafo;
 * - `\` antes de `*`, `-` ou `\` escreve o caractere como ele é.
 *
 * O editor (TipTap) trabalha com o documento ProseMirror em JSON; aqui ficam as conversões puras:
 * o bloco como o DOCX o escreveu → documento do editor; o markup gravado → documento do editor; o
 * documento do editor → markup. O modo diz o que o trecho aceita: `rico` (negrito, itens e
 * parágrafos), `item` (negrito, numa linha de lista) e `simples` (o campo do risco, só texto).
 */
import type { JSONContent } from '@tiptap/core';

import type { ParagrafoDoBlocoData } from './documentoDaSaida';

export type ModoDaEdicao = 'rico' | 'item' | 'simples';

const NEGRITO = '**';
const ITEM = '- ';
const CONTINUACAO = '  ';
const ESCAPE = '\\';
const ESCAPAVEIS = new Set(['*', '-', '\\']);

interface Bloco {
  item: boolean;
  linhas: string[];
}

interface Pedaco {
  texto: string;
  negrito: boolean;
}

/** Os parágrafos e os itens do markup, na ordem, com as linhas de cada um. */
function blocosDo(markup: string): Bloco[] {
  const blocos: Bloco[] = [];
  let atual: Bloco | null = null;
  for (const linha of markup.replaceAll('\r\n', '\n').split('\n')) {
    if (linha.trim() === '') {
      atual = null;
    } else if (linha.startsWith(ITEM)) {
      atual = { item: true, linhas: [linha.slice(ITEM.length)] };
      blocos.push(atual);
    } else if (atual !== null && (!atual.item || linha.startsWith(CONTINUACAO))) {
      atual.linhas.push(atual.item ? linha.slice(CONTINUACAO.length) : linha);
    } else {
      atual = { item: false, linhas: [linha] };
      blocos.push(atual);
    }
  }
  return blocos;
}

/** Os pedaços de uma linha, com o negrito que vem da linha anterior do mesmo bloco. */
function pedacosDaLinha(linha: string, negritoAntes: boolean): [Pedaco[], boolean] {
  const pedacos: Pedaco[] = [];
  let negrito = negritoAntes;
  let atual = '';
  const fechar = () => {
    if (atual !== '') pedacos.push({ texto: atual, negrito });
    atual = '';
  };
  let i = 0;
  while (i < linha.length) {
    const proximo = linha.charAt(i + 1);
    if (linha.charAt(i) === ESCAPE && ESCAPAVEIS.has(proximo)) {
      atual += proximo;
      i += 2;
    } else if (linha.startsWith(NEGRITO, i)) {
      fechar();
      negrito = !negrito;
      i += NEGRITO.length;
    } else {
      atual += linha.charAt(i);
      i += 1;
    }
  }
  fechar();
  return [pedacos, negrito];
}

/** As linhas do bloco em nós do editor: texto (com ou sem negrito) e quebra de linha. */
function inlineDasLinhas(linhas: string[], comNegrito: boolean): JSONContent[] {
  const nos: JSONContent[] = [];
  let negrito = false;
  linhas.forEach((linha, i) => {
    if (i > 0) nos.push({ type: 'hardBreak' });
    const [pedacos, depois] = comNegrito ? pedacosDaLinha(linha, negrito) : [[], false];
    negrito = depois;
    const daLinha = comNegrito ? pedacos : [{ texto: linha, negrito: false }];
    for (const p of daLinha) {
      if (p.texto !== '') nos.push(noDeTexto(p.texto, p.negrito));
    }
  });
  return nos;
}

function noDeTexto(texto: string, negrito: boolean): JSONContent {
  return negrito
    ? { type: 'text', text: texto, marks: [{ type: 'bold' }] }
    : { type: 'text', text: texto };
}

function paragrafo(conteudo: JSONContent[]): JSONContent {
  return conteudo.length > 0 ? { type: 'paragraph', content: conteudo } : { type: 'paragraph' };
}

/** Os parágrafos e os itens em nós do editor: os itens vizinhos numa lista. No modo sem lista, o
 * item vira parágrafo. */
function comAsListas(blocos: { item: boolean; conteudo: JSONContent[] }[], modo: ModoDaEdicao) {
  const nos: JSONContent[] = [];
  for (const bloco of blocos) {
    if (!bloco.item || modo !== 'rico') {
      nos.push(paragrafo(bloco.conteudo));
      continue;
    }
    const item: JSONContent = { type: 'listItem', content: [paragrafo(bloco.conteudo)] };
    const ultimo = nos.at(-1);
    if (ultimo?.type === 'bulletList') ultimo.content?.push(item);
    else nos.push({ type: 'bulletList', content: [item] });
  }
  return { type: 'doc', content: nos.length > 0 ? nos : [{ type: 'paragraph' }] };
}

/** O markup gravado (o rascunho, a nota, o vigente) como documento do editor. */
export function docDoMarkup(markup: string, modo: ModoDaEdicao = 'rico'): JSONContent {
  if (modo === 'simples') {
    const linhas = markup.split('\n');
    return comAsListas(
      linhas.map((linha) => ({ item: false, conteudo: inlineDasLinhas([linha], false) })),
      modo,
    );
  }
  return comAsListas(
    blocosDo(markup).map((b) => ({ item: b.item, conteudo: inlineDasLinhas(b.linhas, true) })),
    modo,
  );
}

/** O bloco como o DOCX o escreveu (o que o papel mostra) como documento do editor. Fonte mono,
 * discreta ou colorida vira texto corrido; só o negrito fica. */
export function docDoBloco(
  paragrafos: readonly ParagrafoDoBlocoData[],
  modo: ModoDaEdicao = 'rico',
): JSONContent {
  const blocos = paragrafos.map((p) => {
    const conteudo: JSONContent[] = [];
    for (const trecho of p.trechos) {
      trecho.texto.split('\n').forEach((pedaco, i) => {
        if (i > 0) conteudo.push({ type: 'hardBreak' });
        if (pedaco !== '') conteudo.push(noDeTexto(pedaco, modo !== 'simples' && trecho.negrito));
      });
    }
    return { item: p.item, conteudo };
  });
  return comAsListas(blocos, modo);
}

function escapar(texto: string): string {
  return texto.replaceAll('\\', '\\\\').replaceAll('*', '\\*');
}

function negritoDe(no: JSONContent): boolean {
  return no.marks?.some((m) => m.type === 'bold') ?? false;
}

/** O conteúdo de um parágrafo do editor em markup: linhas pela quebra de linha. */
function linhasDoParagrafo(no: JSONContent, modo: ModoDaEdicao): string[] {
  const linhas: string[] = [''];
  let negrito = false;
  const fecharNegrito = () => {
    if (negrito) linhas[linhas.length - 1] += NEGRITO;
    negrito = false;
  };
  for (const filho of no.content ?? []) {
    if (filho.type === 'hardBreak') {
      fecharNegrito();
      linhas.push('');
      continue;
    }
    const texto = filho.text ?? '';
    if (modo === 'simples') {
      linhas[linhas.length - 1] += texto;
      continue;
    }
    if (negritoDe(filho) !== negrito) {
      linhas[linhas.length - 1] += NEGRITO;
      negrito = !negrito;
    }
    linhas[linhas.length - 1] += escapar(texto);
  }
  fecharNegrito();
  return linhas;
}

/** O documento do editor em markup (no modo `simples`, o texto como está). */
export function markupDoDoc(doc: JSONContent, modo: ModoDaEdicao = 'rico'): string {
  const blocos: { item: boolean; texto: string }[] = [];
  for (const no of doc.content ?? []) {
    if (no.type === 'bulletList') {
      for (const item of no.content ?? []) {
        const linhas = (item.content ?? []).flatMap((p) => linhasDoParagrafo(p, modo));
        blocos.push({ item: true, texto: ITEM + linhas.join(`\n${CONTINUACAO}`) });
      }
    } else {
      const linhas = linhasDoParagrafo(no, modo);
      const comEscape =
        modo === 'simples' ? linhas : linhas.map((l) => (l.startsWith(ITEM) ? ESCAPE + l : l));
      blocos.push({ item: false, texto: comEscape.join('\n') });
    }
  }
  const comTexto = blocos.filter((b) => b.texto.replace(ITEM, '').trim() !== '');
  if (modo !== 'rico') return comTexto.map((b) => b.texto).join('\n');
  return comTexto
    .map((b, i) => (i === 0 ? '' : b.item && comTexto[i - 1]?.item ? '\n' : '\n\n') + b.texto)
    .join('');
}

/** O texto sem as marcas: o que o documento imprime (para buscar e mostrar). */
export function planoDoMarkup(markup: string): string {
  const blocos = blocosDo(markup);
  const temMarcas =
    markup.includes(NEGRITO) ||
    markup.includes(ESCAPE) ||
    blocos.length > 1 ||
    blocos.some((b) => b.item);
  if (!temMarcas) return markup;
  return blocos
    .map((b, i) => {
      const linhas = b.linhas.map((l) =>
        pedacosDaLinha(l, false)[0]
          .map((p) => p.texto)
          .join(''),
      );
      const separador = i === 0 ? '' : b.item ? '\n' : '\n\n';
      return separador + linhas.join('\n');
    })
    .join('');
}
